import { CommonModule } from '@angular/common'
import { Component, OnInit, inject } from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { Router } from '@angular/router'
import { forkJoin, map, Observable } from 'rxjs'

import {
  CollectionRecord,
  Enums,
  Environment,
  ExpandedHuntPackageRecord,
  ExpandedProfile,
  Hunt,
  HuntTemplate,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import { BaseDialogComponent } from '@hyperkit'
import {
  ButtonComponent,
  ErrorComponent,
  InputComponent,
  LinkComponent,
  MultiSelectComponent,
  NotificationsComponent,
  ReusableDialogComponent,
  SelectComponent,
  TypographyComponent,
  MessageComponent,
} from '@hyperkit'

import { isHuntPackageHuntCompatible } from '../../../../../helpers/utils'
import { HuntAPIService } from '../../../../hunt/services/api/hunt-api.service'
import { CompanyService } from '../../../services/company.service'
import { ContentService } from '../../../services/content.service'
import { EnvironmentService } from '../../../services/environment.service'
import { UserService } from '../../../services/user.service'
// Interface representing compatibility summary for environments and tools
export interface CompatSummary {
  envName: string
  envId: string
  excludedCount: number
  tools: {
    toolId: string
    toolName: string
    missingCategories: string[]
    missingFields?: {
      category: string
      fields: string[]
    }[]
  }[]
}

// Component decorator defining metadata
@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReusableDialogComponent,
    ErrorComponent,
    LinkComponent,
    MultiSelectComponent,
    NotificationsComponent,
    ReactiveFormsModule,
    SelectComponent,
    ButtonComponent,
    InputComponent,
    MessageComponent,
    TypographyComponent,
  ],
  selector: 'new-hunt-dialog',
  templateUrl: './new-hunt-dialog.component.html',
  styleUrls: ['./new-hunt-dialog.component.scss'],
})
export class NewHuntDialogComponent
  extends BaseDialogComponent<
    {
      huntPackages: Array<ExpandedHuntPackageRecord>
      huntTemplate?: HuntTemplate
      collection?: CollectionRecord
    },
    Hunt
  >
  implements OnInit
{
  private formBuilder = inject(FormBuilder)
  private companyService = inject(CompanyService)
  private huntService = inject(HuntAPIService)
  private contentService = inject(ContentService)
  private userService = inject(UserService)
  private environmentService = inject(EnvironmentService)
  private router = inject(Router)

  // Form group to manage the form controls
  public formGroup: FormGroup
  public priorities: Array<string> = []
  public assignees$: Observable<Array<Profile>> // Observable for dropdown users
  public selectedObject: Profile // Selected profile object
  public generatedHuntPackages: ExpandedHuntPackageRecord[]
  public placeholderName = '' // Default placeholder name
  public defaultAssignee: Profile & ExpandedProfile // Default assignee
  public user: Profile // Current user profile
  public error: string // Error message for UI
  public environments: Environment[] // List of environments
  public warnings: CompatSummary[] = [] // Compatibility warnings
  public checkWarnings = false

  public ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      name: [
        this.truncateString(this.placeholderName, 50),
        Validators.required,
      ],
      environments: new FormControl([], [Validators.required]),
      assignee: [this.defaultAssignee ? this.defaultAssignee : null],
      priority: [Enums.PriorityEnum.LOW, Validators.required],
    })

    this.userService.loadProfile().subscribe((user) => {
      this.defaultAssignee = {
        ...user,
        fullName: user.firstName + ' ' + user.lastName,
      }

      this.environmentService.getAllEnvironments().subscribe((envs) => {
        this.environments = envs
        const environments = this.data.huntTemplate?.environments

        // Only after loading options: patch the selected environment IDs
        const envIds = Array.isArray(environments)
          ? environments.map((env) => env.id)
          : []

        this.formGroup.patchValue({ environments: envIds })

        if (envIds.length > 0) {
          this.generateContentForEnv()
        } else {
          this.warnings = []
        }
      })

      if (this.data.huntTemplate?.defaultAssignee) {
        this.defaultAssignee = {
          ...this.data.huntTemplate.defaultAssignee,
          fullName:
            this.data.huntTemplate.defaultAssignee.firstName +
            ' ' +
            this.data.huntTemplate.defaultAssignee.lastName,
        }
      }

      this.formGroup.patchValue({
        assignee: this.defaultAssignee ? this.defaultAssignee : null,
      })
    })

    this.assignees$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) =>
        users.map((user) => ({
          ...user,
          fullName: user.firstName + ' ' + user.lastName,
        })),
      ),
    )

    if (this.data.collection) {
      const date: string = new Date(Date.now()).toLocaleDateString()
      this.placeholderName =
        this.truncateString(this.data.collection.title, 50 - date.length - 1) +
        ' ' +
        date
    } else if (this.data.huntPackages.length === 1) {
      this.placeholderName = this.truncateString(
        this.data.huntPackages[0].title,
        50,
      )
    }

    if (this.data.huntTemplate) {
      const date: string = new Date(Date.now()).toLocaleDateString()
      this.placeholderName =
        this.truncateString(this.data.huntTemplate.name, 50 - date.length - 1) +
        ' ' +
        date
    }

    this.formGroup.patchValue({
      name: this.placeholderName || null,
    })

    this.priorities = Object.values(Enums.PriorityEnum)
  }

  public goToEnvironmentSettings(): void {
    this.router.navigate(['/settings/environment'])
    this.dialogRef.close()
  }

  // Handles form submission
  public onSubmit(): void {
    const hunt: Hunt = this.formGroup.value // Get form values
    hunt.status = Enums.StatusEnum.TO_DO // Set default status

    hunt.environments = this.environments.filter((env) => {
      return this.formGroup.value.environments.includes(env.id)
    })

    // Assign hunt template or generated hunt packages
    if (this.data.huntTemplate) {
      hunt.huntTemplate = this.data.huntTemplate
      hunt.huntPackages = this.data.huntTemplate.huntPackages
      hunt.abstract = this.data.huntTemplate.description
      hunt.hypothesis = this.data.huntTemplate.hypothesis
    } else {
      hunt.huntPackages = this.generatedHuntPackages
      if (this.data.collection) {
        hunt.hypothesis = this.data.collection.synopsis
        hunt.abstract = this.data.collection.threat_summary
      } else {
        if (this.data.huntPackages.length === 1) {
          hunt.abstract = this.data.huntPackages[0].context.threat_description
          hunt.hypothesis = this.data.huntPackages[0].description
        }
      }
    }

    // Call hunt service to save the hunt
    this.huntService.createHunt(hunt).subscribe(
      (hunt: Hunt) => {
        this.dialogRef.close(hunt) // Close the dialog on success
      },
      (error) => {
        this.error = error.message // Show error message on failure
      },
    )
  }

  // Truncate a string to a specified length
  public truncateString(string, limit): string {
    if (string.length > limit) {
      return string.substring(0, limit - 3) + '...'
    } else {
      return string
    }
  }

  // Remove a warning tag
  public removeTag(event): void {
    const tagIndex = this.warnings.findIndex(
      (warning) => warning.envId == event.dataItem.id,
    )
    this.warnings.splice(tagIndex, 1)
  }

  // Handle value change event in environments dropdown
  public onValueChange(event): void {
    if (event && event.length > 0) {
      this.generateContentForEnv()
    } else {
      this.warnings = []
    }
  }

  // Generate content for the selected environments
  public generateContentForEnv(): void {
    // Ensure environments is an array and not null/undefined
    const environments = Array.isArray(this.formGroup?.value?.environments)
      ? this.formGroup.value.environments
      : []

    if (!environments || environments.length === 0) {
      this.warnings = []
      return
    }

    this.checkWarnings = false
    this.warnings = []

    // Validate huntPackages
    const huntPackages = Array.isArray(this.data.huntPackages)
      ? this.data.huntPackages
      : []

    if (huntPackages.length === 0) {
      this.generatedHuntPackages = []
      this.checkWarnings = true
      return
    }

    // Generate observables for compatible hunt packages
    const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
      huntPackages
        .filter((huntPackage) => isHuntPackageHuntCompatible(huntPackage))
        .map((huntPackage) => {
          const contentRequest =
            huntPackage.source === Enums.SourceEnum.CUSTOMER
              ? this.contentService.generateCustomContent(
                  this.data.huntTemplate.id,
                  huntPackage.UUID,
                  {
                    environments: environments,
                  },
                )
              : this.contentService.getHuntPackageContent(huntPackage.UUID, {
                  environments: environments,
                })

          return contentRequest.pipe(
            map((generatedContent: Environment[]) => {
              generatedContent = generatedContent.map((env) => {
                env.tools = env.tools.filter((tool) => {
                  return (
                    tool.generatedContent &&
                    tool.generatedContent?.Hunt?.length > 0
                  )
                })

                // Track unmatched content as warnings
                env.tools.forEach((tool) => {
                  tool.generatedContent.Hunt?.forEach((content) => {
                    if (!content.matched) {
                      this.addWarning(
                        env.id,
                        env.name,
                        tool.id,
                        tool.name,
                        content,
                      )
                    }
                  })
                })

                env.tools = env.tools.filter(
                  (tool) =>
                    tool.generatedContent?.Hunt &&
                    tool.generatedContent.Hunt.length > 0,
                )

                return env
              })

              const expanded = huntPackage as ExpandedHuntPackageRecord
              expanded.analysis = expanded.originalAnalysis = generatedContent
              return expanded
            }),
          )
        })

    if (!generatedHuntPackages || generatedHuntPackages.length === 0) {
      this.generatedHuntPackages = []
      this.checkWarnings = true
      return
    }

    // Process the generated observables using forkJoin
    forkJoin(generatedHuntPackages).subscribe({
      next: (generatedHuntPacks) => {
        // Deduplicate warnings
        this.warnings.forEach((env) => {
          env.tools.forEach((tool) => {
            tool.missingCategories = [...new Set(tool.missingCategories)]
            tool.missingFields?.forEach((field) => {
              field.fields = [...new Set(field.fields)]
            })
          })
        })
        this.generatedHuntPackages = generatedHuntPacks
        this.checkWarnings = true
      },
      error: () => {
        this.error = 'Failed to generate content for environments.'
        this.checkWarnings = true
      },
    })
  }

  private addWarning(
    envId: string,
    envName: string,
    toolId: string,
    toolName: string,
    content: any,
  ): void {
    let env = this.warnings.find((e) => e.envId === envId)
    if (!env) {
      env = { envId, envName, excludedCount: 0, tools: [] }
      this.warnings.push(env)
    }
    env.excludedCount++

    let tool = env.tools.find((t) => t.toolId === toolId)
    if (!tool) {
      tool = { toolId, toolName, missingCategories: [], missingFields: [] }
      env.tools.push(tool)
    }

    if (content.missingFields?.length > 0) {
      const existing = tool.missingFields.find(
        (f) => f.category === content.category,
      )
      if (existing) {
        existing.fields.push(...content.missingFields)
      } else {
        tool.missingFields.push({
          category: content.category,
          fields: content.missingFields,
        })
      }
    } else {
      tool.missingCategories.push(content.category)
    }
  }

  // Close the dialog
  public onClose(): void {
    this.dialogRef.close()
  }

  protected initializeOptions(): void {
    this.options = { headerText: 'New Hunt' }
  }
}
